import { useState, useCallback } from 'react'
import type { PaymentLinkFormData } from '@shared/types/payment-links'
import { getInitialPaymentLinkFormData, transformFormDataToPaymentLink } from '@shared/types/payment-links'
import { paymentLinkService } from '@shared/services/payment-link.service'
import { toast } from 'sonner'
import envConfig from '@/config/env.config'

interface PaymentLinkFormHook {
  formData: PaymentLinkFormData
  isLoading: boolean
  updateFormData: (updates: Partial<PaymentLinkFormData>) => void
  handleSave: (editingId?: string) => Promise<boolean>
  setFormData: (formData: PaymentLinkFormData) => void
}

export function usePaymentLinkForm(): PaymentLinkFormHook {
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState<PaymentLinkFormData>(getInitialPaymentLinkFormData())

  const updateFormData = useCallback((updates: Partial<PaymentLinkFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
  }, [])

  const handleSave = useCallback(async (editingId?: string): Promise<boolean> => {
    // Validate required fields
    // Title is only required for cause-service flow (products flow uses product names)
    if (formData.paymentFor === 'cause-service' && !formData.paymentPage.title.trim()) {
      toast.error('Please enter a payment page title')
      return false
    }

    // Short code is optional — only validate if user explicitly set one
    if (formData.useCustomShortCode && !formData.shortCode.trim()) {
      toast.error('Please enter a short code')
      return false
    }

    if (!formData.paymentFor) {
      toast.error('Please select what this payment is for')
      return false
    }

    // Validate payment type specific fields
    if (formData.paymentFor === 'products-subscriptions') {
      if (!formData.selectedProducts || formData.selectedProducts.length === 0) {
        toast.error('Please add at least one product')
        return false
      }
    }

    // Validate amount limits if enabled
    if (formData.setAmountLimits) {
      if (formData.minimumAmount && formData.maximumAmount) {
        const min = parseFloat(formData.minimumAmount)
        const max = parseFloat(formData.maximumAmount)
        if (min > max) {
          toast.error('Minimum amount cannot be greater than maximum amount')
          return false
        }
      }
    }

    // Validate redirect URL if provided
    if (formData.afterPaymentBehavior === 'redirect' && formData.redirectUrl) {
      try {
        new URL(formData.redirectUrl)
      } catch {
        toast.error('Please enter a valid redirect URL')
        return false
      }
    }

    // Validate tax percentage if tax support enabled (backend requirement)
    if (formData.advanced.allowBusinessTaxIds) {
      // Tax percentage would need to be added to form data
      // For now, this validation is commented out until the field is added
      // if (!formData.taxPercentage) {
      //   toast.error('Tax percentage is required when tax support is enabled')
      //   return false
      // }
    }

    // Validate terms URL if terms acceptance required (backend requirement)
    // This field would need to be added to the form
    // if (formData.advanced.requireTermsAcceptance && !formData.termsUrl) {
    //   toast.error('Terms URL is required when terms acceptance is enabled')
    //   return false
    // }

    setIsLoading(true)
    try {
      const paymentLinkData = transformFormDataToPaymentLink(formData)

      const DEFAULT_ORG_ID = envConfig.defaultOrgId
      const DEFAULT_ACCOUNT_ID = String(envConfig.defaultAccountId)

      if (editingId) {
        // Update existing payment link
        await paymentLinkService.updatePaymentLink(
          DEFAULT_ORG_ID,
          DEFAULT_ACCOUNT_ID,
          editingId,
          paymentLinkData
        )
        toast.success('Payment link updated successfully')
      } else {
        // Create new payment link
        await paymentLinkService.createPaymentLink(
          DEFAULT_ORG_ID,
          DEFAULT_ACCOUNT_ID,
          paymentLinkData
        )
        toast.success('Payment link created successfully')
      }

      return true
    } catch (error) {
      console.error('Failed to save payment link:', error)
      toast.error(editingId ? 'Failed to update payment link' : 'Failed to create payment link')
      return false
    } finally {
      setIsLoading(false)
    }
  }, [formData])

  return {
    formData,
    isLoading,
    updateFormData,
    handleSave,
    setFormData
  }
}
